import { useState } from 'react';
import { UserCircle, Activity, Plug, Users, Folder, MessageSquare, Network, GraduationCap, Zap, FolderOpen, Database, Globe, Monitor, Search, Plus, Play, Pause, MoreHorizontal, Check, Clock, ArrowUpRight, ChevronDown, X, Lock, Layers, KeyRound, Copy, Pencil, Grid3x3 } from 'lucide-react';
import { TRACKS, GROUP_NAMES } from '../../data/workspaceTracks';
import { PulsesView } from './PulsesView';

// ── In-shell workspace views ─────────────────────────────────────────────────
// Every nav destination in the Giga chat shell renders here rather than routing
// out to the old dashboard. One shell, many views: the sidebar swaps the pane,
// nothing navigates.
// ─────────────────────────────────────────────────────────────────────────────

export type WorkspaceViewId = 'assistants' | 'pulses' | 'connections' | 'groups' | 'tracks' | 'chats' | 'workflows' | 'skills' | 'power-ups' | 'files' | 'tables' | 'browser' | 'viewer';
export const VIEW_META: Record<WorkspaceViewId, {
  title: string;
  blurb: string;
  icon: typeof UserCircle;
  action?: string;
}> = {
  assistants: {
    title: 'Assistants',
    blurb: 'Coworkers you brief once, then delegate to.',
    icon: UserCircle,
    action: 'New assistant'
  },
  pulses: {
    title: 'Pulses',
    blurb: 'Work that runs on a schedule without being asked.',
    icon: Activity,
    action: 'New pulse'
  },
  connections: {
    title: 'Connections',
    blurb: 'Accounts Giga signs into on your behalf.',
    icon: Plug,
    action: 'Connect a tool'
  },
  groups: {
    title: 'Groups',
    blurb: 'Who can see which knowledge. Access follows the group.',
    icon: Users,
    action: 'New group'
  },
  tracks: {
    title: 'Tracks',
    blurb: 'Projects and areas that keep their work together.',
    icon: Folder,
    action: 'New track'
  },
  chats: {
    title: 'Chats',
    blurb: 'Every conversation, searchable.',
    icon: MessageSquare
  },
  workflows: {
    title: 'Workflows',
    blurb: 'Multi-step jobs you run on demand.',
    icon: Network,
    action: 'New workflow'
  },
  skills: {
    title: 'Skills',
    blurb: 'Recorded how-tos Giga reuses across jobs.',
    icon: GraduationCap,
    action: 'Record a skill'
  },
  'power-ups': {
    title: 'Power Ups',
    blurb: 'Extra abilities you can switch on per workspace.',
    icon: Zap
  },
  files: {
    title: 'Files',
    blurb: 'Documents Giga can read and cite.',
    icon: FolderOpen,
    action: 'Upload'
  },
  tables: {
    title: 'Tables',
    blurb: 'Structured data it can query and write rows to.',
    icon: Database,
    action: 'New table'
  },
  browser: {
    title: 'Browser',
    blurb: 'Sites Giga operates on your behalf.',
    icon: Globe
  },
  viewer: {
    title: 'Viewer',
    blurb: 'Watch a run happen live, step by step.',
    icon: Monitor
  }
};
const logo = (slug: string) => `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
const ASSISTANTS = [{
  name: 'Inbox triage',
  desc: 'Sorts overnight mail and surfaces only what needs a reply.',
  tools: ['gmail', 'googlecalendar'],
  runs: '412 runs',
  group: 'Faith’s Private Space',
  private: true,
  active: true
}, {
  name: 'Pipeline analyst',
  desc: 'Watches deal movement and flags renewals going quiet.',
  tools: ['salesforce', 'slack'],
  runs: '96 runs',
  group: 'Giga Master',
  private: false,
  active: true
}, {
  name: 'Meeting prep',
  desc: 'Builds a one-pager for every external meeting the night before.',
  tools: ['googlecalendar', 'notion'],
  runs: '188 runs',
  group: 'Giga Marketing',
  private: false,
  active: true
}, {
  name: 'Support summariser',
  desc: 'Turns Intercom threads into weekly themes for product.',
  tools: ['intercom', 'linear'],
  runs: '54 runs',
  group: 'NoContact Ops',
  private: false,
  active: false
}];

// The catalogue. `accounts` matters more than a boolean: several tools support
// more than one signed-in account, and knowing there are two is the difference
// between "connected" and "connected as the wrong person".
type ConnCategory = 'Communication' | 'Productivity' | 'CRM & Sales' | 'Engineering' | 'Marketing' | 'Finance' | 'Data' | 'Design' | 'Support';
const CONNECTIONS: {
  name: string;
  slug: string;
  desc: string;
  category: ConnCategory;
  accounts: number;
  account?: string;
  popular?: boolean;
}[] = [{
  name: 'Gmail',
  slug: 'gmail',
  desc: 'Read, draft and send mail from your own address.',
  category: 'Communication',
  accounts: 2,
  account: 'sarah.chen@brightwave.io',
  popular: true
}, {
  name: 'Google Drive',
  slug: 'google-drive',
  desc: 'Search and read documents across shared drives.',
  category: 'Productivity',
  accounts: 1,
  account: 'sarah.chen@brightwave.io',
  popular: true
}, {
  name: 'Google Calendar',
  slug: 'google-calendar',
  desc: 'See availability and schedule on your behalf.',
  category: 'Productivity',
  accounts: 1,
  account: 'sarah.chen@brightwave.io',
  popular: true
}, {
  name: 'Notion',
  slug: 'notion',
  desc: 'A collaborative workspace for docs and wikis.',
  category: 'Productivity',
  accounts: 1,
  account: 'Brightwave workspace',
  popular: true
}, {
  name: 'Slack',
  slug: 'slack',
  desc: 'Workplace messaging — post, read and reply in threads.',
  category: 'Communication',
  accounts: 1,
  account: 'brightwave.slack.com',
  popular: true
}, {
  name: 'Linear',
  slug: 'linear',
  desc: 'Product development and issue tracking.',
  category: 'Engineering',
  accounts: 1,
  account: 'brightwave',
  popular: true
}, {
  name: 'Google Sheets',
  slug: 'google-sheets',
  desc: 'Read and write rows in spreadsheets.',
  category: 'Data',
  accounts: 1,
  account: 'sarah.chen@brightwave.io'
}, {
  name: 'Intercom',
  slug: 'intercom',
  desc: 'Customer service platform for support conversations.',
  category: 'Support',
  accounts: 1,
  account: 'Brightwave'
}, {
  name: 'Salesforce',
  slug: 'salesforce',
  desc: 'The CRM of record for accounts and opportunities.',
  category: 'CRM & Sales',
  accounts: 0,
  popular: true
}, {
  name: 'HubSpot',
  slug: 'hubspot',
  desc: 'Customer platform and CRM for marketing and sales.',
  category: 'CRM & Sales',
  accounts: 0,
  popular: true
}, {
  name: 'Jira',
  slug: 'jira',
  desc: 'Issue and project tracking for software teams.',
  category: 'Engineering',
  accounts: 0,
  popular: true
}, {
  name: 'GitHub',
  slug: 'github',
  desc: 'Code hosting, pull requests and issues.',
  category: 'Engineering',
  accounts: 0
}, {
  name: 'Figma',
  slug: 'figma',
  desc: 'Collaborative interface design and prototyping.',
  category: 'Design',
  accounts: 0
}, {
  name: 'Airtable',
  slug: 'airtable',
  desc: 'Structured bases Giga can query and update.',
  category: 'Data',
  accounts: 0
}, {
  name: 'Stripe',
  slug: 'stripe',
  desc: 'Financial infrastructure — payments and subscriptions.',
  category: 'Finance',
  accounts: 0
}, {
  name: 'Zoom',
  slug: 'zoom',
  desc: 'Meetings, recordings and transcripts.',
  category: 'Communication',
  accounts: 0
}, {
  name: 'Asana',
  slug: 'asana',
  desc: 'Work management for tasks and projects.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'Mailchimp',
  slug: 'mailchimp',
  desc: 'Email campaigns, audiences and reporting.',
  category: 'Marketing',
  accounts: 0
}, {
  name: 'Dropbox',
  slug: 'dropbox',
  desc: 'File storage Giga can read from and write to.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'QuickBooks',
  slug: 'quickbooks',
  desc: 'Accounting, invoices and expense records.',
  category: 'Finance',
  accounts: 0
}, {
  name: 'Canva',
  slug: 'canva',
  desc: 'Visual design and collaboration platform.',
  category: 'Design',
  accounts: 0
}, {
  name: 'Monday.com',
  slug: 'monday',
  desc: 'Work management platform for teams.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'ClickUp',
  slug: 'clickup',
  desc: 'Tasks, docs and goals in one workspace.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'Zapier',
  slug: 'zapier',
  desc: 'Trigger automations across thousands of apps.',
  category: 'Productivity',
  accounts: 1,
  account: 'brightwave-ops'
}, {
  name: 'Vercel',
  slug: 'vercel',
  desc: 'Deployments, previews and build logs.',
  category: 'Engineering',
  accounts: 0
}, {
  name: 'Sentry',
  slug: 'sentry',
  desc: 'Error monitoring and release health.',
  category: 'Engineering',
  accounts: 1,
  account: 'brightwave'
}, {
  name: 'Supabase',
  slug: 'supabase',
  desc: 'Backend platform with a Postgres database.',
  category: 'Data',
  accounts: 0
}, {
  name: 'Cloudflare',
  slug: 'cloudflare',
  desc: 'DNS, caching and edge configuration.',
  category: 'Engineering',
  accounts: 0
}, {
  name: 'Ahrefs',
  slug: 'ahrefs',
  desc: 'Search rankings, backlinks and keyword data.',
  category: 'Marketing',
  accounts: 1,
  account: 'brightwave.io'
}, {
  name: 'Apollo',
  slug: 'apollo',
  desc: 'Sales intelligence and prospect enrichment.',
  category: 'CRM & Sales',
  accounts: 0
}, {
  name: 'Clay',
  slug: 'clay',
  desc: 'Enrich and research accounts at scale.',
  category: 'CRM & Sales',
  accounts: 0
}, {
  name: 'Webflow',
  slug: 'webflow',
  desc: 'Website building, CMS and publishing.',
  category: 'Marketing',
  accounts: 0
}, {
  name: 'Ramp',
  slug: 'ramp',
  desc: 'Corporate cards, spend and receipts.',
  category: 'Finance',
  accounts: 0
}, {
  name: 'Zendesk',
  slug: 'zendesk',
  desc: 'Ticketing and helpdesk for customer support.',
  category: 'Support',
  accounts: 0
}, {
  name: 'Granola',
  slug: 'granola',
  desc: 'AI notepad that captures meeting notes.',
  category: 'Productivity',
  accounts: 1,
  account: 'sarah.chen@brightwave.io'
}, {
  name: 'Confluence',
  slug: 'confluence',
  desc: 'Team wiki and internal documentation.',
  category: 'Productivity',
  accounts: 0
}];
const CONN_CATEGORIES: ConnCategory[] = ['Communication', 'Productivity', 'CRM & Sales', 'Engineering', 'Marketing', 'Finance', 'Data', 'Design', 'Support'];
const CONN_PAGE_SIZE = 12;

// Which group owns each live credential. Only connected tools appear here —
// an integration nobody has signed into doesn't belong to anyone yet.
const CONN_GROUP: Record<string, string> = {
  Gmail: 'Everyone',
  'Google Drive': 'Everyone',
  'Google Calendar': 'Everyone',
  Notion: 'Giga Master',
  Slack: 'Everyone',
  Linear: 'Giga Master',
  'Google Sheets': 'Giga Marketing',
  Intercom: 'NoContact Ops',
  Zapier: 'NoContact Ops',
  Sentry: 'Giga Master',
  Ahrefs: 'Giga Marketing',
  Granola: "Faith's Private Space"
};

// Custom credentials: the escape hatch for anything without a first-party
// integration. Each type asks for a different shape of secret.
const CREDENTIAL_TYPES = [{
  id: 'email',
  label: 'Email & Password',
  desc: 'For tools Giga signs into through the browser.',
  fields: [{
    label: 'Login URL',
    placeholder: 'https://app.example.com/login',
    type: 'url'
  }, {
    label: 'Email',
    placeholder: 'you@company.com',
    type: 'email'
  }, {
    label: 'Password',
    placeholder: '••••••••••••',
    type: 'password'
  }]
}, {
  id: 'api',
  label: 'API Key',
  desc: 'For tools with a token-based API.',
  fields: [{
    label: 'Base URL',
    placeholder: 'https://api.example.com',
    type: 'url'
  }, {
    label: 'API key',
    placeholder: 'sk_live_…',
    type: 'password'
  }, {
    label: 'Header name',
    placeholder: 'Authorization',
    type: 'text'
  }]
}, {
  id: 'sql-string',
  label: 'SQL (connection string)',
  desc: 'One URI for the whole database.',
  fields: [{
    label: 'Connection string',
    placeholder: 'postgres://user:pass@host:5432/db',
    type: 'password'
  }]
}, {
  id: 'sql-fields',
  label: 'SQL (fields)',
  desc: 'Host, port and credentials entered separately.',
  fields: [{
    label: 'Host',
    placeholder: 'db.example.com',
    type: 'text'
  }, {
    label: 'Port',
    placeholder: '5432',
    type: 'text'
  }, {
    label: 'Database',
    placeholder: 'production',
    type: 'text'
  }, {
    label: 'User',
    placeholder: 'readonly',
    type: 'text'
  }, {
    label: 'Password',
    placeholder: '••••••••••••',
    type: 'password'
  }]
}] as const;
const CHATS = [{
  name: 'Inbox catch-up',
  when: 'Today, 09:12',
  preview: 'Three things need you today — Acme redlines, Northwind seats, invoice 2214.'
}, {
  name: 'Tomorrow’s meetings',
  when: 'Today, 08:40',
  preview: 'Four meetings. Two need prep — Acme legal and Northwind kickoff.'
}, {
  name: 'Q3 pipeline review',
  when: 'Yesterday',
  preview: 'Revenue up 34% QoQ, CAC down 12%, one renewal unsigned.'
}, {
  name: 'Renewal risk in Salesforce',
  when: 'Yesterday',
  preview: 'Two accounts have gone quiet for 21+ days.'
}, {
  name: 'Draft reply to Acme legal',
  when: '2 days ago',
  preview: 'Position on the liability cap, plus data residency language.'
}, {
  name: 'Summarise the board deck',
  when: '3 days ago',
  preview: 'Slide 19 has an ask with no numbers behind it.'
}, {
  name: 'Who owns the migration?',
  when: 'Last week',
  preview: 'Priya, per the Linear project and the #eng thread.'
}, {
  name: 'Weekly marketing recap',
  when: 'Last week',
  preview: 'Paid down 8%, organic up 15%, two campaigns ended.'
}];
const WORKFLOWS = [{
  name: 'Tweet → Notion research table',
  steps: 4,
  runs: 128,
  success: '96%',
  last: '2 hours ago'
}, {
  name: 'New lead → enriched CRM record',
  steps: 6,
  runs: 341,
  success: '99%',
  last: '18 minutes ago'
}, {
  name: 'Support thread → Linear ticket',
  steps: 3,
  runs: 87,
  success: '92%',
  last: 'Yesterday'
}, {
  name: 'Invoice → finance tracker row',
  steps: 5,
  runs: 64,
  success: '100%',
  last: '4 days ago'
}];
const SKILLS = [{
  name: 'File a Linear bug from a support thread',
  used: 'Used 44 times',
  learned: 'Recorded by Priya'
}, {
  name: 'Build a weekly pipeline recap',
  used: 'Used 31 times',
  learned: 'Recorded by Sarah'
}, {
  name: 'Enrich a lead from a company domain',
  used: 'Used 128 times',
  learned: 'Recorded by Marcus'
}, {
  name: 'Draft a renewal follow-up',
  used: 'Used 19 times',
  learned: 'Recorded by Sarah'
}];
const POWER_UPS = [{
  name: 'Computer use',
  desc: 'Operates sites that have no API, in a sandboxed browser.',
  on: true
}, {
  name: 'Deep research',
  desc: 'Multi-source research runs that cite every claim.',
  on: true
}, {
  name: 'Voice notes',
  desc: 'Transcribes and acts on voice messages.',
  on: false
}, {
  name: 'Code interpreter',
  desc: 'Runs analysis on spreadsheets and CSV exports.',
  on: false
}];
const FILES = [{
  name: 'Q3 Board Review.pdf',
  meta: '24 slides',
  size: '4.2 MB',
  group: 'Giga Master',
  visibility: 'Shared',
  when: 'Yesterday'
}, {
  name: 'Acme MSA redlines.docx',
  meta: '18 pages',
  size: '0.24 MB',
  group: 'No Contact Private',
  visibility: 'Private',
  when: '2 days ago'
}, {
  name: 'Pricing model v4.xlsx',
  meta: '9 sheets',
  size: '1.1 MB',
  group: 'Giga Master',
  visibility: 'Shared',
  when: 'Last week'
}, {
  name: 'Meeting notes — Northwind.md',
  meta: '2 pages',
  size: '0.03 MB',
  group: "Faith's Private Space",
  visibility: 'Private',
  when: '3 days ago'
}, {
  name: 'Brand guidelines.pdf',
  meta: '32 pages',
  size: '8.4 MB',
  group: 'Giga Marketing',
  visibility: 'Shared',
  when: 'Last month'
}];
const TABLES = [{
  name: 'Pipeline',
  rows: '1,284 rows',
  cols: '18 columns',
  source: 'Salesforce',
  group: 'Giga Master'
}, {
  name: 'Content calendar',
  rows: '96 rows',
  cols: '11 columns',
  source: 'Notion',
  group: 'Giga Marketing'
}, {
  name: 'Competitor research',
  rows: '312 rows',
  cols: '14 columns',
  source: 'Giga native',
  group: 'Giga Marketing'
}, {
  name: 'Invoice tracker',
  rows: '208 rows',
  cols: '9 columns',
  source: 'Airtable',
  group: 'NoContact Ops'
}];

// Per-group counts. Deep, muted tile colours so seven monograms stay
// distinguishable without turning the list into a paint chart.
const GROUP_DETAIL: Record<string, {
  path: string;
  color: string;
  members: number;
  chats: number;
  tracks: number;
  credentials: number;
  files: number;
}> = {
  'Giga Master': {
    path: 'spaces/operator',
    color: '#1F3A5F',
    members: 3,
    chats: 214,
    tracks: 33,
    credentials: 0,
    files: 0
  },
  'Giga Marketing': {
    path: 'spaces/operator-gtm',
    color: '#22303C',
    members: 4,
    chats: 89,
    tracks: 20,
    credentials: 5,
    files: 13
  },
  Everyone: {
    path: 'spaces/everyone',
    color: '#8E1F45',
    members: 21,
    chats: 79,
    tracks: 2,
    credentials: 8,
    files: 5
  },
  'No Contact Private': {
    path: 'spaces/no-contact-private',
    color: '#332C7A',
    members: 2,
    chats: 20,
    tracks: 3,
    credentials: 4,
    files: 28
  },
  'NoContact Ops': {
    path: 'spaces/no-contact-ops',
    color: '#8C2340',
    members: 3,
    chats: 17,
    tracks: 4,
    credentials: 6,
    files: 4
  },
  "Faith's Private Space": {
    path: 'spaces/faith-private',
    color: '#4A3A24',
    members: 1,
    chats: 35,
    tracks: 1,
    credentials: 5,
    files: 7
  },
  Demo: {
    path: 'spaces/entropy',
    color: '#7A1F32',
    members: 2,
    chats: 4,
    tracks: 0,
    credentials: 0,
    files: 0
  }
};
const BROWSER_SITES = [{
  name: 'portal.acme-legal.com',
  desc: 'Downloads countersigned contracts',
  runs: '12 runs'
}, {
  name: 'app.quickbooks.com',
  desc: 'Pulls invoice status for the finance tracker',
  runs: '64 runs'
}, {
  name: 'suppliers.northwind.io',
  desc: 'Checks stock levels weekly',
  runs: '8 runs'
}];

// ── Small shared pieces ──────────────────────────────────────────────────────

function Card({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="rounded-2xl border border-[#EFEAE1] bg-white p-5" data-id="element-3814" data-mp="3u8ere72pgl4q">{children}</div>;
}
function Row({
  children
}: {
  children: React.ReactNode;
}) {
  return <li className="flex items-center gap-4 border-b border-[#F4F0E9] px-5 py-3.5 last:border-b-0" data-id="element-3815" data-mp="19f1jsx4dsgyd">
      {children}
    </li>;
}
function List({
  children
}: {
  children: React.ReactNode;
}) {
  return <ul className="overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white" data-id="element-3816" data-mp="37yg59713az9p">
      {children}
    </ul>;
}
function StatusDot({
  on
}: {
  on: boolean;
}) {
  return <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${on ? 'bg-emerald-500' : 'bg-neutral-300'}`} data-id="element-3817" data-mp="3hnzoidspsv53" />;
}
function ToolLogo({
  slug,
  size = 15
}: {
  slug: string;
  size?: number;
}) {
  return <img src={logo(slug)} alt="" aria-hidden="true" style={{
    width: size,
    height: size
  }} className="object-contain" data-id="element-3818" data-mp="3rl4bgfudwumy" />;
}

// ── The view itself ──────────────────────────────────────────────────────────

export function WorkspaceViews({
  view
}: {
  view: WorkspaceViewId;
}) {
  const [query, setQuery] = useState('');
  const [connTab, setConnTab] = useState<'all' | 'popular' | 'connected'>('all');
  const [connCategory, setConnCategory] = useState<ConnCategory | 'all'>('all');
  const [connCatOpen, setConnCatOpen] = useState(false);
  const [connGroup, setConnGroup] = useState<string>('all');
  const [connPage, setConnPage] = useState(1);
  const [customOpen, setCustomOpen] = useState(false);
  const [customType, setCustomType] = useState<(typeof CREDENTIAL_TYPES)[number]['id'] | null>(null);
  const meta = VIEW_META[view];
  // Pulses carries its own list + detail surface, so it owns the whole pane.
  if (view === 'pulses') return <PulsesView data-id="element-3819" data-mp="2hmurkliddp7v" />;
  const searchable = view === 'tracks' || view === 'chats' || view === 'files' || view === 'connections' || view === 'groups';
  const q = query.trim().toLowerCase();
  return <div className="mx-auto max-w-4xl px-6 pb-16 pt-20 md:pt-16" data-id="element-3820" data-mp="27nvkv8h603ni">
      <header className="flex flex-wrap items-end justify-between gap-4 pb-6" data-id="element-3821" data-mp="23omnzuqtvpnq">
        <div data-id="element-3822" data-mp="wa1e5i48m8kg">
          <h1 className="font-title text-[26px] font-medium tracking-tight text-[#2B211A] md:text-[30px]" data-id="element-3823" data-mp="wajkx8oj056m">
            {meta.title}
          </h1>
          <p className="mt-1.5 text-[14.5px] text-neutral-500" data-id="element-3824" data-mp="sasnim4huqt4">{meta.blurb}</p>
        </div>
        {view === 'connections' ?
      /* Anything without a first-party integration comes in here, so
      the control names the secret it needs rather than just "Add". */
      <div className="relative" data-id="element-3825" data-mp="3f0u5kue7f56m">
            <button type="button" onClick={() => setCustomOpen(o => !o)} aria-expanded={customOpen} className="inline-flex items-center gap-1.5 rounded-xl border border-[#EFEAE1] bg-white px-3.5 py-2 text-[13.5px] font-bold text-[#2B211A] transition-colors hover:border-[#2B211A]" data-id="element-3826" data-mp="3expx3ktphna5">
              <Plus size={14} strokeWidth={2.5} data-id="element-3827" data-mp="6ksc6nnr2oed" />
              Add custom
              <ChevronDown size={14} className={`text-neutral-400 transition-transform duration-200 ${customOpen ? 'rotate-180' : ''}`} data-id="element-3828" data-mp="2hcju3ipra7h" />
            </button>
            {customOpen && <>
                <div className="fixed inset-0 z-30" onClick={() => setCustomOpen(false)} data-id="element-3829" data-mp="2n8wexhjqtzxx" />
                <div className="absolute right-0 top-[calc(100%+6px)] z-40 w-[280px] overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white shadow-[0_24px_60px_-24px_rgba(43,33,26,0.35)]" data-id="element-3830" data-mp="2i9a41ere0vsa">
                  <p className="border-b border-[#F4F0E9] px-4 py-2.5 text-[11.5px] font-bold text-[#A2917F]" data-id="element-3831" data-mp="12q2d260rxhh5">
                    Credential type
                  </p>
                  {CREDENTIAL_TYPES.map(t => <button key={t.id} type="button" onClick={() => {
              setCustomType(t.id);
              setCustomOpen(false);
            }} className="block w-full px-4 py-2.5 text-left transition-colors hover:bg-[#FFF7F0]" data-id="element-3832" data-mp="1bjfig7g0ybk4">
                      <span className="block text-[13.5px] font-semibold text-[#2B211A]" data-id="element-3833" data-mp="1lfikzeesgc7l">
                        {t.label}
                      </span>
                      <span className="block text-[12px] text-neutral-500" data-id="element-3834" data-mp="1ui9xlhjehe0t">
                        {t.desc}
                      </span>
                    </button>)}
                </div>
              </>}
          </div> : meta.action && <button type="button" className="inline-flex items-center gap-1.5 rounded-xl bg-[#2B211A] px-3.5 py-2 text-[13.5px] font-bold text-white transition-colors hover:bg-black" data-id="element-3835" data-mp="4s4lf087oye">
              <Plus size={14} strokeWidth={2.5} data-id="element-3836" data-mp="ygcwx4iah3wu" />
              {meta.action}
            </button>}
      </header>

      {/* The form for the chosen credential type, inline above the list so the
       catalogue stays visible behind the decision. */}
      {view === 'connections' && customType && (() => {
      const t = CREDENTIAL_TYPES.find(c => c.id === customType)!;
      return <div className="mb-4 rounded-2xl border border-[#EFEAE1] bg-white p-5" data-id="element-3837" data-mp="1hefitafjikqf">
              <div className="flex items-start justify-between gap-3" data-id="element-3838" data-mp="1hcl3cbn1fktq">
                <div data-id="element-3839" data-mp="1w2savwbibjfn">
                  <h2 className="text-[15px] font-semibold text-[#2B211A]" data-id="element-3840" data-mp="1w54u0ct03zua">
                    Add custom tool · {t.label}
                  </h2>
                  <p className="mt-1 text-[13px] text-neutral-500" data-id="element-3841" data-mp="2551nqsu8zow7">{t.desc}</p>
                </div>
                <button type="button" onClick={() => setCustomType(null)} aria-label="Cancel" className="text-neutral-300 transition-colors hover:text-[#2B211A]" data-id="element-3842" data-mp="205yhj52j7cmn">
                  <X size={16} data-id="element-3843" data-mp="2ops19172jb77" />
                </button>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-3844" data-mp="2onpeqr54k078">
                <label className="block sm:col-span-2" data-id="element-3845" data-mp="2aoz9go85iu7s">
                  <span className="mb-1.5 block text-[12.5px] font-semibold text-[#2B211A]" data-id="element-3846" data-mp="2erm22rcncj61">
                    Name
                  </span>
                  <input placeholder="What should Giga call this?" className="w-full rounded-xl border border-[#EFEAE1] px-3 py-2.5 text-[14px] text-[#2B211A] outline-none transition-colors placeholder:text-neutral-400 focus:border-[#2B211A]" data-id="element-3847" data-mp="20zfmp7hbig7n" />
                </label>
                {t.fields.map(f => <label key={f.label} className={`block ${t.fields.length === 1 ? 'sm:col-span-2' : ''}`} data-id="element-3848" data-mp="1w0cpg97kxea8">
                    <span className="mb-1.5 block text-[12.5px] font-semibold text-[#2B211A]" data-id="element-3849" data-mp="1i2eivpzi2m5a">
                      {f.label}
                    </span>
                    <input type={f.type} placeholder={f.placeholder} className="w-full rounded-xl border border-[#EFEAE1] px-3 py-2.5 text-[14px] text-[#2B211A] outline-none transition-colors placeholder:text-neutral-400 focus:border-[#2B211A]" data-id="element-3850" data-mp="1ezqqqqul53x0" />
                  </label>)}
              </div>
              <div className="mt-4 flex items-center justify-between gap-4 border-t border-[#F4F0E9] pt-3.5" data-id="element-3851" data-mp="157kqyuy3di79">
                <p className="flex items-center gap-1.5 text-[12px] text-neutral-500" data-id="element-3852" data-mp="w992aygscuhg">
                  <Lock size={12} className="text-neutral-400" data-id="element-3853" data-mp="w6wcjz4sdftu" />
                  Stored in the vault. Never shown in a chat.
                </p>
                <button type="button" className="rounded-xl bg-[#2B211A] px-4 py-2 text-[13.5px] font-bold text-white transition-colors hover:bg-black" data-id="element-3854" data-mp="mc4deqvk2kwy">
                  Save credential
                </button>
              </div>
            </div>;
    })()}

      {searchable && <div className="relative mb-4" data-id="element-3855" data-mp="2cns64m8g57zq">
          <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-3856" data-mp="2cots3lr03ew8" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder={`Search ${meta.title.toLowerCase()}…`} className="w-full rounded-xl border border-[#EFEAE1] bg-white py-2.5 pl-10 pr-4 text-[14px] text-[#2B211A] outline-none transition-colors placeholder:text-neutral-400 focus:border-[#2B211A]" data-id="element-3857" data-mp="3ukvmboo6fxrf" />
        </div>}

      {/* Assistants — cards, because each one has a brief worth reading. */}
      {view === 'assistants' && <div className="grid gap-3 sm:grid-cols-2" data-id="element-3858" data-mp="3gl2vzugkbjjz">
          {ASSISTANTS.map(a => <Card key={a.name} data-id="element-3859" data-mp="3gngsm7qw53tn">
              <div className="flex items-start justify-between gap-3" data-id="element-3860" data-mp="3kmg46b74nyu2">
                <span className="flex items-center gap-2" data-id="element-3861" data-mp="3kpalvuyrb5js">
                  <StatusDot on={a.active} data-id="element-3862" data-mp="3pmu04uvgfibq" />
                  <span className="text-[15px] font-semibold text-[#2B211A]" data-id="element-3863" data-mp="zwqqi1i5uj2y">
                    {a.name}
                  </span>
                </span>
                <button type="button" aria-label={`More options for ${a.name}`} className="text-neutral-300 transition-colors hover:text-[#2B211A]" data-id="element-3864" data-mp="14uieikfopl1v">
                  <MoreHorizontal size={16} data-id="element-3865" data-mp="2wxcysk39n4vg" />
                </button>
              </div>
              {/* Who can see it comes before the description: an assistant
               someone else owns is read differently to one of your own. */}
              <p className="mt-1.5 flex items-center gap-1.5 text-[12.5px] text-neutral-500" data-id="element-3866" data-mp="35zcjpnheit3o">
                {a.private ? <Lock size={11} className="shrink-0 text-neutral-400" data-id="element-3867" data-mp="2i9037o83bo1h" /> : <Users size={11} className="shrink-0 text-neutral-400" data-id="element-3868" data-mp="2d9ne0wtqy4vq" />}
                <span className="truncate" data-id="element-3869" data-mp="2d7j520gzqfol">{a.group}</span>
                {a.private && <span className="shrink-0 rounded-full bg-[#F4F0E9] px-1.5 py-px text-[10.5px] font-semibold text-neutral-500" data-id="element-3870" data-mp="2rzutd3ogj7s7">
                    Private
                  </span>}
              </p>
              <p className="mt-2.5 text-[13.5px] leading-relaxed text-neutral-500" data-id="element-3871" data-mp="1zge8j78x63ih">
                {a.desc}
              </p>
              <div className="mt-4 flex items-center justify-between gap-3 border-t border-[#F4F0E9] pt-3" data-id="element-3872" data-mp="1uh1nms1lz6ij">
                <span className="flex items-center gap-1.5" data-id="element-3873" data-mp="erk0myxinghb">
                  {a.tools.map(t => <ToolLogo key={t} slug={t} data-id="element-3874" data-mp="etn0eaoh0nz8" />)}
                </span>
                <span className="text-[12px] text-neutral-400" data-id="element-3875" data-mp="11gmkv8sit2y">{a.runs}</span>
              </div>
            </Card>)}
        </div>}

      {view === 'connections' && <>
          {/* Three ways in: what most teams start with, what's already live,
           and the full catalogue. Counts sit inline so the tabs double as a
           read on how far along the workspace is. */}
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 border-b border-[#EFEAE1]" data-id="element-3876" data-mp="jrg7r71ifo13">
            <div className="flex items-center gap-5" data-id="element-3877" data-mp="oool8ubpr3w0">
              {([['all', 'All integrations', CONNECTIONS.length], ['popular', 'Popular', CONNECTIONS.filter(c => c.popular).length], ['connected', 'Connected', CONNECTIONS.filter(c => c.accounts > 0).length]] as const).map(([id, label, count]) => <button key={id} type="button" onClick={() => {
            setConnTab(id);
            setConnPage(1);
          }} className={`-mb-px flex items-center gap-1.5 border-b-2 pb-2.5 text-[13.5px] font-semibold transition-colors ${connTab === id ? 'border-[#2B211A] text-[#2B211A]' : 'border-transparent text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-3878" data-mp="40wh7hf6gy7m">
                  {label}
                  <span className="text-[12.5px] font-medium text-neutral-400" data-id="element-3879" data-mp="nowww0oig0wg">
                    {count}
                  </span>
                </button>)}
            </div>
            {/* Category narrows a long catalogue by the kind of work a tool does,
             which is how people look for one they haven't connected yet. */}
            <div className="relative -mb-px pb-1.5" data-id="element-3880" data-mp="8z7r1s5h38pu">
              <button type="button" onClick={() => setConnCatOpen(o => !o)} aria-expanded={connCatOpen} className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[13px] font-medium text-neutral-500 transition-colors hover:text-[#2B211A]" data-id="element-3881" data-mp="16fn1puxn5xry">
                {connCategory === 'all' ? 'Category' : connCategory}
                <ChevronDown size={14} className={`text-neutral-400 transition-transform duration-200 ${connCatOpen ? 'rotate-180' : ''}`} data-id="element-3882" data-mp="1dzp7bv7hd3kw" />
              </button>
              {connCatOpen && <>
                  <div className="fixed inset-0 z-30" onClick={() => setConnCatOpen(false)} data-id="element-3883" data-mp="1nxv7o8dn21nn" />
                  <div className="absolute right-0 top-[calc(100%+2px)] z-40 w-[190px] overflow-hidden rounded-xl border border-[#EFEAE1] bg-white py-1 shadow-[0_20px_50px_-20px_rgba(43,33,26,0.35)]" data-id="element-3884" data-mp="1rxduxasxmotn">
                    {(['all', ...CONN_CATEGORIES] as const).map(cat => <button key={cat} type="button" onClick={() => {
                setConnCategory(cat as ConnCategory | 'all');
                setConnCatOpen(false);
                setConnPage(1);
              }} className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[13.5px] font-medium text-[#2B211A] transition-colors hover:bg-[#FFF7F0]" data-id="element-3885" data-mp="21qkxyp079sqk">
                        <span className="truncate" data-id="element-3886" data-mp="3if8bxrk52f10">
                          {cat === 'all' ? 'All categories' : cat}
                        </span>
                        <span className="flex shrink-0 items-center gap-1.5" data-id="element-3887" data-mp="38fr8zhw9uf8q">
                          <span className="text-[12px] text-neutral-400" data-id="element-3888" data-mp="3ddsndpir5qdv">
                            {cat === 'all' ? CONNECTIONS.length : CONNECTIONS.filter(c => c.category === cat).length}
                          </span>
                          {connCategory === cat && <Check size={13} strokeWidth={3} className="text-[#EA580C]" data-id="element-3889" data-mp="9wskd33pk5wz" />}
                        </span>
                      </button>)}
                  </div>
                </>}
            </div>
          </div>

          {/* Connected only: filter by the group that owns the credential.
           Monogram tiles rather than a dropdown — with a handful of groups the
           colours are recognised faster than names are read. */}
          {connTab === 'connected' && <div className="mb-3 flex items-center gap-1.5 overflow-x-auto pb-1" data-id="element-3890" data-mp="2dd724w7gscm8">
              <button type="button" onClick={() => {
          setConnGroup('all');
          setConnPage(1);
        }} aria-label="All groups" className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition-colors ${connGroup === 'all' ? 'border-[#2B211A] bg-[#F4F0E9] text-[#2B211A]' : 'border-[#EFEAE1] text-neutral-400 hover:border-[#2B211A]'}`} data-id="element-3891" data-mp="28fnre69z6hz1">
                <Grid3x3 size={15} data-id="element-3892" data-mp="1pgjhkvam0k6b" />
              </button>
              {GROUP_NAMES.filter(g => CONNECTIONS.some(c => c.accounts > 0 && CONN_GROUP[c.name] === g)).map(g => {
          const n = CONNECTIONS.filter(c => c.accounts > 0 && CONN_GROUP[c.name] === g).length;
          return <button key={g} type="button" onClick={() => {
            setConnGroup(g);
            setConnPage(1);
          }} title={`${g} · ${n} connected`} className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[11px] font-bold tracking-tight text-white transition-transform ${connGroup === g ? 'ring-2 ring-[#2B211A] ring-offset-2' : 'opacity-75 hover:opacity-100'}`} style={{
            backgroundColor: GROUP_DETAIL[g].color
          }} data-id="element-3893" data-mp="1zgtxworxy7pp">
                      {g.split(' ').slice(0, 2).map(w => w[0]).join('')}
                      <span className="absolute -right-1 -top-1 flex h-[15px] min-w-[15px] items-center justify-center rounded-full border border-white bg-[#F4F0E9] px-1 text-[9.5px] font-bold text-[#2B211A]" data-id="element-3894" data-mp="2lhyelqmqbdsc">
                        {n}
                      </span>
                    </button>;
        })}
            </div>}

          {(() => {
        const matches = CONNECTIONS.filter(c => connTab === 'all' || (connTab === 'popular' ? c.popular : c.accounts > 0)).filter(c => connTab !== 'connected' || connGroup === 'all' || CONN_GROUP[c.name] === connGroup).filter(c => connCategory === 'all' || c.category === connCategory).filter(c => !q || c.name.toLowerCase().includes(q) || c.desc.toLowerCase().includes(q));
        const pages = Math.max(1, Math.ceil(matches.length / CONN_PAGE_SIZE));
        const page = Math.min(connPage, pages);
        const start = (page - 1) * CONN_PAGE_SIZE;
        const shown = matches.slice(start, start + CONN_PAGE_SIZE);
        return <>
                {/* Count first, then pagination — you need to know how big the
                 result is before deciding whether to page or refine. */}
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3" data-id="element-3895" data-mp="euhhdbk915kf">
                  <p className="text-[12.5px] text-neutral-500" data-id="element-3896" data-mp="9v4p2balz6ag">
                    {matches.length === 0 ? 'No integrations match' : <>
                        Showing {start + 1}–{Math.min(start + CONN_PAGE_SIZE, matches.length)} of{' '}
                        {matches.length} integration
                        {matches.length === 1 ? '' : 's'}
                      </>}
                  </p>
                  {pages > 1 && <div className="flex items-center gap-1" data-id="element-3897" data-mp="1vx370skhzo6n">
                      <button type="button" onClick={() => setConnPage(p => Math.max(1, p - 1))} disabled={page === 1} className="rounded-lg px-2.5 py-1 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:bg-[#F4F0E9] disabled:pointer-events-none disabled:text-neutral-300" data-id="element-3898" data-mp="1vzpllc5t33yd">
                        Previous
                      </button>
                      <span className="px-1.5 text-[12.5px] text-neutral-500" data-id="element-3899" data-mp="1d626w17n5mli">
                        Page {page} of {pages}
                      </span>
                      <button type="button" onClick={() => setConnPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="rounded-lg px-2.5 py-1 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:bg-[#F4F0E9] disabled:pointer-events-none disabled:text-neutral-300" data-id="element-3900" data-mp="138xjjy227l34">
                        Next
                      </button>
                    </div>}
                </div>

                {matches.length === 0 ? <div className="rounded-2xl border border-[#EFEAE1] bg-white px-6 py-12 text-center" data-id="element-3901" data-mp="qy6he41oos46">
                    <p className="text-[14px] font-semibold text-[#2B211A]" data-id="element-3902" data-mp="m0eiur6ja7r0">
                      Nothing here
                    </p>
                    <p className="mx-auto mt-1.5 max-w-[38ch] text-[13px] leading-relaxed text-neutral-500" data-id="element-3903" data-mp="10pu5buz17r0w">
                      Try another category, or add it yourself with a custom
                      credential.
                    </p>
                  </div> :
          /* Two columns: a catalogue is scanned for a name, and a single
           tall column makes the eye travel much further to find one. */
          <div className="grid gap-2.5 sm:grid-cols-2" data-id="element-3904" data-mp="1jlaruojjpync">
                    {shown.map(c => <div key={c.name} className="group flex items-center gap-3 rounded-2xl border border-[#EFEAE1] bg-white px-4 py-3 transition-colors hover:border-[#DED6C9]" data-id="element-3905" data-mp="1eoty9etkus2v">
                        <ToolLogo slug={c.slug} size={20} data-id="element-3906" data-mp="3ggyuv3m0udjx" />
                        <span className="min-w-0 flex-1" data-id="element-3907" data-mp="9yoknro38s52">
                          <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3908" data-mp="a081pkhkj77w">
                            {c.name}
                          </span>
                          {/* Accounts, not a boolean — two accounts is a
                           materially different state. The specific account is
                           held back until hover so the grid stays quiet. */}
                          {c.accounts > 0 ? <>
                              <span className="mt-0.5 flex items-center gap-1.5 text-[12.5px] text-neutral-500 group-hover:hidden" data-id="element-3909" data-mp="wyclrplcizc0">
                                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" data-id="element-3910" data-mp="11q4hsfn9tx9j" />
                                {c.accounts} account
                                {c.accounts === 1 ? '' : 's'} connected
                              </span>
                              <span className="mt-0.5 hidden truncate text-[12.5px] text-neutral-500 group-hover:block" data-id="element-3911" data-mp="1gmc1zfyoiyot">
                                {c.accounts === 1 && c.account ? c.account : `${c.category} · managed in ${CONN_GROUP[c.name] ?? 'Everyone'}`}
                              </span>
                            </> : <span className="mt-0.5 block truncate text-[12.5px] text-neutral-500" data-id="element-3912" data-mp="2r5wyz3l8wi4g">
                              {c.desc}
                            </span>}
                        </span>
                        <button type="button" className="shrink-0 rounded-lg border border-transparent px-2.5 py-1.5 text-[12.5px] font-bold text-[#2B211A] opacity-0 transition-opacity hover:border-[#2B211A] group-hover:opacity-100" data-id="element-3913" data-mp="3jvngznq0045u">
                          {c.accounts > 0 ? 'Manage' : 'Connect'}
                        </button>
                      </div>)}
                  </div>}
              </>;
      })()}
        </>}

      {/* Groups — the counts ARE the content: they tell you at a glance which
       group actually holds the company's knowledge. Metrics sit as quiet
       pills on the right so scanning the names stays effortless. */}
      {view === 'groups' && <List data-id="element-3914" data-mp="365r75p0bgolw">
          {GROUP_NAMES.filter(g => !q || g.toLowerCase().includes(q)).map(g => {
        const d = GROUP_DETAIL[g];
        const shared = !g.toLowerCase().includes('private');
        return <Row key={g} data-id="element-3915" data-mp="2x52sqt70gm8h">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[12px] font-bold tracking-tight text-white" style={{
            backgroundColor: d.color
          }} data-id="element-3916" data-mp="325782fw6nax4">
                    {g.split(' ').slice(0, 2).map(w => w[0]).join('')}
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-3917" data-mp="3fy6kua4kyr0z">
                    <span className="flex items-center gap-2" data-id="element-3918" data-mp="3fzhswm526kxw">
                      <span className="truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3919" data-mp="3kv6sgph2w1u7">
                        {g}
                      </span>
                      {!shared && <span className="shrink-0 rounded-full bg-[#F4F0E9] px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-neutral-500" data-id="element-3920" data-mp="29h1ovxks1tze">
                          Private
                        </span>}
                    </span>
                    <span className="mt-0.5 block truncate font-mono text-[12px] text-neutral-400" data-id="element-3921" data-mp="1wy7cnitzd2sc">
                      {d.path}
                    </span>
                  </span>
                  <span className="hidden shrink-0 items-center gap-1 sm:flex" data-id="element-3922" data-mp="260gp4e8t768d">
                    {([[Users, d.members, 'members'], [MessageSquare, d.chats, 'chats'], [Layers, d.tracks, 'tracks'], [KeyRound, d.credentials, 'credentials'], [Copy, d.files, 'files']] as const).map(([Icon, n, label]) => <span key={label} title={`${n} ${label}`} className="inline-flex items-center gap-1 rounded-full bg-[#F7F4EF] px-2 py-1 text-[12px] font-medium text-neutral-500" data-id="element-3923" data-mp="1rzw3labiewb6">
                        <Icon size={12} className="text-neutral-400" data-id="element-3924" data-mp="zj1wxwddb5sn" />
                        {n}
                      </span>)}
                  </span>
                  <button type="button" aria-label={`Edit ${g}`} className="shrink-0 text-neutral-300 transition-colors hover:text-[#2B211A]" data-id="element-3925" data-mp="ujp7d3v43kmd">
                    <Pencil size={15} data-id="element-3926" data-mp="3mzt3zrouh1r5" />
                  </button>
                </Row>;
      })}
        </List>}

      {view === 'tracks' && <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-3927" data-mp="4swmsc5hxdbc">
          {TRACKS.filter(t => t.status === 'active').filter(t => !q || t.name.toLowerCase().includes(q)).map(t => <button key={t.name} type="button" className="rounded-2xl border border-[#EFEAE1] bg-white p-4 text-left transition-colors hover:border-[#2B211A]" data-id="element-3928" data-mp="397wdtt6ql1xe">
                <span className="flex items-center justify-between gap-2" data-id="element-3929" data-mp="346yomwnb77me">
                  <Folder size={16} className="text-neutral-400" data-id="element-3930" data-mp="3i19fbjiv9bv3" />
                  <StatusDot on data-id="element-3931" data-mp="3d97x7gz0oqx5" />
                </span>
                <span className="mt-3 block truncate text-[14px] font-semibold text-[#2B211A]" data-id="element-3932" data-mp="3d7n47vscpg1e">
                  {t.name}
                </span>
                <span className="mt-0.5 block truncate text-[12px] text-neutral-500" data-id="element-3933" data-mp="2ljd89lf2n390">
                  {t.group} · {t.type}
                </span>
              </button>)}
        </div>}

      {view === 'chats' && <List data-id="element-3934" data-mp="2g5lj455g6f3a">
          {CHATS.filter(c => !q || c.name.toLowerCase().includes(q) || c.preview.toLowerCase().includes(q)).map(c => <Row key={c.name} data-id="element-3935" data-mp="21hs86xrb40r1">
                <span className="min-w-0 flex-1" data-id="element-3936" data-mp="21eme143tm8og">
                  <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3937" data-mp="26gli6g7dx9mg">
                    {c.name}
                  </span>
                  <span className="mt-0.5 block truncate text-[12.5px] text-neutral-500" data-id="element-3938" data-mp="2u3a73luta2ck">
                    {c.preview}
                  </span>
                </span>
                <span className="shrink-0 text-[12px] text-neutral-400" data-id="element-3939" data-mp="2k6f7ezmsxvwp">
                  {c.when}
                </span>
              </Row>)}
        </List>}

      {view === 'workflows' && <List data-id="element-3940" data-mp="18qqk275myuvd">
          {WORKFLOWS.map(w => <Row key={w.name} data-id="element-3941" data-mp="18twabpq042so">
              <span className="min-w-0 flex-1" data-id="element-3942" data-mp="1dqmryhhgf6g7">
                <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3943" data-mp="1dsfwnr8ibglk">
                  {w.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-neutral-500" data-id="element-3944" data-mp="3begvyy1czf65">
                  {w.steps} steps · {w.runs} runs · {w.success} success · last{' '}
                  {w.last}
                </span>
              </span>
              <button type="button" aria-label={`Run ${w.name}`} className="shrink-0 rounded-lg border border-[#EFEAE1] px-3 py-1.5 text-[12.5px] font-bold text-[#2B211A] transition-colors hover:border-[#2B211A]" data-id="element-3945" data-mp="31nm6lkstfzgh">
                Run
              </button>
            </Row>)}
        </List>}

      {view === 'skills' && <List data-id="element-3946" data-mp="2npnu6ky22d3m">
          {SKILLS.map(s => <Row key={s.name} data-id="element-3947" data-mp="2nqyz4o38lqwb">
              <GraduationCap size={16} className="shrink-0 text-neutral-400" data-id="element-3948" data-mp="2iqb7c076p611" />
              <span className="min-w-0 flex-1" data-id="element-3949" data-mp="lxhqpi7a8m0i">
                <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3950" data-mp="lw6o3lpkpkaz">
                  {s.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-neutral-500" data-id="element-3951" data-mp="gwlpjcebfdys">
                  {s.learned}
                </span>
              </span>
              <span className="shrink-0 text-[12px] text-neutral-400" data-id="element-3952" data-mp="tfz788dkt12z">
                {s.used}
              </span>
            </Row>)}
        </List>}

      {view === 'power-ups' && <div className="grid gap-3 sm:grid-cols-2" data-id="element-3953" data-mp="137db0sck8n1x">
          {POWER_UPS.map(p => <Card key={p.name} data-id="element-3954" data-mp="138ocummg3kqe">
              <div className="flex items-start justify-between gap-3" data-id="element-3955" data-mp="5jv31jqq3oyz">
                <span className="text-[15px] font-semibold text-[#2B211A]" data-id="element-3956" data-mp="ai6d9vkxk1hq">
                  {p.name}
                </span>
                <span className={`mt-0.5 flex h-[18px] w-[32px] shrink-0 items-center rounded-full px-[3px] transition-colors ${p.on ? 'justify-end bg-[#2B211A]' : 'justify-start bg-[#E7E1D6]'}`} data-id="element-3957" data-mp="fgzrxrbydyjh">
                  <span className="h-3 w-3 rounded-full bg-white" data-id="element-3958" data-mp="1qwxxvmbu44gh" />
                </span>
              </div>
              <p className="mt-2 text-[13.5px] leading-relaxed text-neutral-500" data-id="element-3959" data-mp="1vupuuurc6grj">
                {p.desc}
              </p>
            </Card>)}
        </div>}

      {view === 'files' && <List data-id="element-3960" data-mp="1pfipqavqnknw">
          {FILES.filter(f => !q || f.name.toLowerCase().includes(q)).map(f => <Row key={f.name} data-id="element-3961" data-mp="23f0if1c5ni2j">
                <FolderOpen size={16} className="shrink-0 text-neutral-400" data-id="element-3962" data-mp="23fa8sq143r8r" />
                <span className="min-w-0 flex-1" data-id="element-3963" data-mp="1yg73d4h7zj09">
                  <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3964" data-mp="1yeckrwtf82zc">
                    {f.name}
                  </span>
                  <span className="mt-0.5 block text-[12.5px] text-neutral-500" data-id="element-3965" data-mp="28ed7ztuev38t">
                    {f.meta} · {f.group}
                  </span>
                </span>
                {/* Size and who can see it — the two things you check before
                 sharing or deleting, so they sit on the row itself. */}
                <span className="hidden shrink-0 items-center gap-3 text-[12.5px] text-neutral-500 sm:flex" data-id="element-3966" data-mp="2w2wafsqs4lsz">
                  <span data-id="element-3967" data-mp="2r5w6pohejv51">{f.size}</span>
                  <span className="inline-flex items-center gap-1.5" data-id="element-3968" data-mp="2r2ruq4xp3v3t">
                    <Copy size={12} className="text-neutral-400" data-id="element-3969" data-mp="3aqwevxq7qcs2" />
                    {f.visibility}
                  </span>
                </span>
                <span className="shrink-0 text-[12px] text-neutral-400" data-id="element-3970" data-mp="30sgxwnkwgjwa">
                  {f.when}
                </span>
              </Row>)}
        </List>}

      {view === 'tables' && <List data-id="element-3971" data-mp="35tebsnyvmrrz">
          {TABLES.map(t => <Row key={t.name} data-id="element-3972" data-mp="2r1vr4cv2o92a">
              <Database size={16} className="shrink-0 text-neutral-400" data-id="element-3973" data-mp="2r1lzylyajvv0" />
              <span className="min-w-0 flex-1" data-id="element-3974" data-mp="2vtdrozt6o7mq">
                <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3975" data-mp="2vv8e6ik3s7sx">
                  {t.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-neutral-500" data-id="element-3976" data-mp="2n0ajp71b4mzh">
                  {t.rows} · {t.cols} · {t.group}
                </span>
              </span>
              <span className="shrink-0 rounded-full bg-[#F4F0E9] px-2.5 py-1 text-[11.5px] font-medium text-neutral-600" data-id="element-3977" data-mp="285vx6wijavg2">
                {t.source}
              </span>
            </Row>)}
        </List>}

      {view === 'browser' && <List data-id="element-3978" data-mp="1p6rd8gb7u2p1">
          {BROWSER_SITES.map(s => <Row key={s.name} data-id="element-3979" data-mp="1p5q1saqafe6s">
              <Globe size={16} className="shrink-0 text-neutral-400" data-id="element-3980" data-mp="1p5g7iay7mkva" />
              <span className="min-w-0 flex-1" data-id="element-3981" data-mp="1k8pwi219ifgq">
                <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3982" data-mp="1f9d4l2vjai7a">
                  {s.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-neutral-500" data-id="element-3983" data-mp="2cwdagy8127ue">
                  {s.desc}
                </span>
              </span>
              <span className="shrink-0 text-[12px] text-neutral-400" data-id="element-3984" data-mp="22yfmmbu2yd2b">
                {s.runs}
              </span>
            </Row>)}
        </List>}

      {/* Viewer — one live run, because that is the whole point of the screen. */}
      {view === 'viewer' && <div className="overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white" data-id="element-3985" data-mp="rl3o8gh3amnj">
          <div className="flex items-center justify-between gap-3 border-b border-[#F4F0E9] px-5 py-3.5" data-id="element-3986" data-mp="mjo7h0ly32ay">
            <span className="flex items-center gap-2" data-id="element-3987" data-mp="2y9dww9th48jx">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#EA580C]" data-id="element-3988" data-mp="336cmqg63ttcb" />
              <span className="text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3989" data-mp="385yy0k2dgzih">
                New lead → enriched CRM record
              </span>
            </span>
            <span className="text-[12.5px] text-neutral-400" data-id="element-3990" data-mp="3cy07xzc2ie5c">
              Step 4 of 6 · 00:38
            </span>
          </div>
          <ol className="divide-y divide-[#F4F0E9]" data-id="element-3991" data-mp="3lxdkj0vhniua">
            {[{
          label: 'Read the inbound form submission',
          state: 'done'
        }, {
          label: 'Looked up the company domain',
          state: 'done'
        }, {
          label: 'Pulled headcount and funding from the web',
          state: 'done'
        }, {
          label: 'Writing the record to Salesforce',
          state: 'running'
        }, {
          label: 'Assigning an owner by territory',
          state: 'pending'
        }, {
          label: 'Posting a summary to #sales',
          state: 'pending'
        }].map(s => <li key={s.label} className="flex items-center gap-3 px-5 py-3" data-id="element-3992" data-mp="2av3dtmb22w0">
                {s.state === 'done' ? <Check size={14} strokeWidth={3} className="shrink-0 text-emerald-500" data-id="element-3993" data-mp="h012zip21v4f" /> : s.state === 'running' ? <span className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-[#EA580C] border-t-transparent" data-id="element-3994" data-mp="bzv6np7ubkg0" /> : <span className="h-3.5 w-3.5 shrink-0 rounded-full border-2 border-[#EFEAE1]" data-id="element-3995" data-mp="3kfeyqkwrywth" />}
                <span className={`text-[14px] ${s.state === 'pending' ? 'text-neutral-400' : 'text-[#2B211A]'}`} data-id="element-3996" data-mp="3fhwxzhswvnwk">
                  {s.label}
                </span>
              </li>)}
          </ol>
          <div className="flex items-center justify-between gap-3 border-t border-[#F4F0E9] px-5 py-3" data-id="element-3997" data-mp="3pf1kjiqo5ldw">
            <span className="text-[12.5px] text-neutral-500" data-id="element-3998" data-mp="19ndnlnbnnsv3">
              412 credits spent so far
            </span>
            <button type="button" className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[#2B211A] transition-colors hover:text-[#EA580C]" data-id="element-3999" data-mp="14okb9y83u7wi">
              Open full trace
              <ArrowUpRight size={13} data-id="element-4000" data-mp="1h8pqwbofq4u5" />
            </button>
          </div>
        </div>}
    </div>;
}